import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ListOperatingExpensesResponse,
  OperatingExpenseResponse,
  OperatingExpenseRecurrenceResponse,
  type CreateOperatingExpenseRequest,
  type UpdateOperatingExpenseRequest,
  type CreateOperatingExpenseRecurrenceRequest,
  type UpdateOperatingExpenseRecurrenceRequest,
  type OperatingExpense,
  type OperatingExpenseCategory,
} from '@sourcito/schemas';
import { apiFetch } from './api';
import { WAREHOUSE_BILLING_QUERY_KEYS } from './warehouse';

// ─────────────────────────────────────────────────────────
// Operating expenses ledger (R1 item 6, 2026-09-01 design doc) — overhead
// that is not COGS and not a warehouse-invoice cost. Hooks mirror
// warehouse.ts's period-expense-attestation hooks (:403-434); every
// mutation invalidates the operating-expenses list, finance-summary (whose
// income gate the ledger feeds), and the warehouse attestation list (D3:
// one attestation button covers both ledgers, so editing either one can
// make an existing attestation stale).
// ─────────────────────────────────────────────────────────

export interface ExpenseRange {
  from: string;
  to: string;
}

/** Six months ending the current UTC calendar month, inclusive. */
export function defaultExpenseRange(now: Date = new Date()): ExpenseRange {
  const to = monthString(now.getUTCFullYear(), now.getUTCMonth());
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5, 1));
  const from = monthString(start.getUTCFullYear(), start.getUTCMonth());
  return { from, to };
}

function monthString(year: number, monthIndex0: number): string {
  const d = new Date(Date.UTC(year, monthIndex0, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export interface ExpenseMonthGroup {
  month: string;
  subtotalCents: number;
  expenses: OperatingExpense[];
}

/** Group ledger rows by month with a per-month subtotal, newest month first. */
export function groupExpensesByMonth(expenses: readonly OperatingExpense[]): ExpenseMonthGroup[] {
  const byMonth = new Map<string, OperatingExpense[]>();
  for (const row of expenses) {
    const list = byMonth.get(row.month);
    if (list) list.push(row);
    else byMonth.set(row.month, [row]);
  }
  return [...byMonth.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([month, rows]) => ({
      month,
      subtotalCents: rows.reduce((sum, row) => sum + row.amountCents, 0),
      expenses: rows,
    }));
}

const CATEGORY_LABELS: Record<OperatingExpenseCategory, string> = {
  contractor_labor: 'Contractor & labor',
  software: 'Software',
  marketing: 'Marketing',
  bank_card_fees: 'Bank & card fees',
  professional_services: 'Professional services',
  other: 'Other',
};

export function formatCategory(category: OperatingExpenseCategory): string {
  return CATEGORY_LABELS[category];
}

/** cents -> a two-decimal dollar string suitable for a text input's value. */
export function centsToDollarsInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** A dollar-input string -> positive integer cents, or null if invalid
 * (negative, zero, or not a number). Accepts "650" and "650.00" alike. */
export function dollarsInputToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars <= 0) return null;
  const cents = Math.round(dollars * 100);
  return cents > 0 ? cents : null;
}

export interface ExpenseRangeTransition {
  range: ExpenseRange;
  error: string | null;
}

/** Apply a month-range control edit without ever replacing a valid range
 * silently. Unlike FinancePeriod (finance-summary.ts), the current UTC
 * month is a valid endpoint here — expenses for the month in progress are
 * knowable ahead (D2). */
export function transitionExpenseRange(
  range: ExpenseRange,
  field: 'from' | 'to',
  value: string
): ExpenseRangeTransition {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    return { range, error: 'Enter a valid calendar month.' };
  }
  const from = field === 'from' ? value : range.from;
  const to = field === 'to' ? value : range.to;
  if (from > to) {
    return {
      range,
      error:
        field === 'from'
          ? 'Start month must be on or before end month.'
          : 'End month must be on or after start month.',
    };
  }
  return { range: { from, to }, error: null };
}

export const OPERATING_EXPENSE_QUERY_KEYS = {
  list: (from: string, to: string) => ['operating-expenses', from, to] as const,
};

function invalidateOperatingExpenseQueries(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['operating-expenses'] });
  void qc.invalidateQueries({ queryKey: ['finance-summary'] });
  void qc.invalidateQueries({
    queryKey: WAREHOUSE_BILLING_QUERY_KEYS.periodExpenseAttestations(),
  });
}

export function useOperatingExpenses(range: ExpenseRange, enabled = true) {
  return useQuery({
    queryKey: OPERATING_EXPENSE_QUERY_KEYS.list(range.from, range.to),
    enabled,
    queryFn: () =>
      apiFetch(
        `/api/operating-expenses?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
        { responseSchema: ListOperatingExpensesResponse }
      ),
  });
}

export function useCreateOperatingExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOperatingExpenseRequest) =>
      apiFetch('/api/operating-expenses', {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: OperatingExpenseResponse,
      }),
    onSuccess: () => invalidateOperatingExpenseQueries(qc),
  });
}

export function useUpdateOperatingExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOperatingExpenseRequest }) =>
      apiFetch(`/api/operating-expenses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        responseSchema: OperatingExpenseResponse,
      }),
    onSuccess: () => invalidateOperatingExpenseQueries(qc),
  });
}

export function useDeleteOperatingExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<null>(`/api/operating-expenses/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateOperatingExpenseQueries(qc),
  });
}

export function useCreateOperatingExpenseRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOperatingExpenseRecurrenceRequest) =>
      apiFetch('/api/operating-expenses/recurrences', {
        method: 'POST',
        body: JSON.stringify(body),
        responseSchema: OperatingExpenseRecurrenceResponse,
      }),
    onSuccess: () => invalidateOperatingExpenseQueries(qc),
  });
}

export function useUpdateOperatingExpenseRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOperatingExpenseRecurrenceRequest }) =>
      apiFetch(`/api/operating-expenses/recurrences/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        responseSchema: OperatingExpenseRecurrenceResponse,
      }),
    onSuccess: () => invalidateOperatingExpenseQueries(qc),
  });
}

export function useDeleteOperatingExpenseRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<null>(`/api/operating-expenses/recurrences/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateOperatingExpenseQueries(qc),
  });
}
